import type { Selector } from 'react-redux';

import type { RootState } from 'features/app/store/store';

const MIN_INTERVAL_MS = 10;
const DEFAULT_TIMEOUT_MS = 3000;

const now = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

/**
 * Polls a selector against the current store state until a predicate returns
 * true or a timeout is reached.
 *
 * Behavior:
 * - The `selector` is invoked once immediately; if its value satisfies
 *   `predicate` the function resolves immediately.
 * - Otherwise the selector is polled every `intervalMs` milliseconds (clamped
 *   to a minimum of 10ms) until `predicate` returns true or `timeoutMs` is
 *   exceeded.
 *
 * Options (all optional):
 * - `timeoutMs` (number): maximum milliseconds to wait before rejecting
 *   (default: 3000). Values < 0 are treated as 0 (immediate timeout).
 * - `intervalMs` (number): requested polling interval in milliseconds
 *   (default: 100). Values below 10ms are clamped to 10ms to avoid busy loops.
 * - `predicate` ((value: T) => boolean): test function for selected value
 *   (default: truthiness check via `Boolean(value)`).
 *
 * Throws:
 * - `Error('waitForSelector: timeout')` when the predicate isn't satisfied
 *    within `timeoutMs`.
 * - Any error thrown by the provided `selector` will propagate through.
 */
export async function waitForSelector<T>(
  getState: () => RootState,
  selector: Selector<RootState, T>,
  options?: {
    timeoutMs?: number;
    intervalMs?: number;
    predicate?: (value: T) => boolean;
  }
): Promise<T> {
  // Set safe defaults and clamp values
  const timeoutMs = Math.max(0, options?.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const intervalMs = Math.max(MIN_INTERVAL_MS, options?.intervalMs ?? 100);

  // Default predicate checks truthiness when no predicate is provided
  const predicate: (v: T) => boolean = options?.predicate ?? Boolean;
  const start = now();

  let value = selector(getState());
  let satisfied = predicate(value);

  while (!satisfied) {
    const current = now();
    if (current - start >= timeoutMs) break;

    const remaining = timeoutMs - (current - start);
    const waitMs = Math.min(intervalMs, Math.max(0, remaining));
    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, waitMs));
    value = selector(getState());
    satisfied = predicate(value);
  }

  if (!satisfied) {
    const elapsed = now() - start;
    throw new Error(
      `waitForSelector: timeout after ${elapsed}ms (timeoutMs=${timeoutMs})`
    );
  }

  return value;
}
